import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, Truck, ShieldCheck, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        api.get('/products'),
        api.get('/categories')
      ]);
      setFeaturedProducts(productsRes.data.slice(0, 4));
      setCategories(categoriesRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-primary text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1768796373708-e1b62a0f2900')] bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-primary"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 uppercase tracking-tight" data-testid="hero-heading">
              Industrial Hardware Solutions You Can Trust
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 mb-8">
              Premium quality tools and hardware for construction, manufacturing, and industrial projects. Order online with fast delivery tracking.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider h-12 px-8 shadow-md" data-testid="shop-now-button">
                  Shop Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold uppercase h-12 px-8" data-testid="contact-us-button">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-slate-100 rounded-sm p-6 hover:shadow-md transition-shadow" data-testid="feature-quality">
              <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center mb-4">
                <ShieldCheck className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2 uppercase">Quality Assured</h3>
              <p className="text-sm text-slate-600">Premium industrial-grade products with manufacturer warranty</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-sm p-6 hover:shadow-md transition-shadow" data-testid="feature-delivery">
              <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center mb-4">
                <Truck className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2 uppercase">Live Tracking</h3>
              <p className="text-sm text-slate-600">Real-time GPS tracking for all deliveries</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-sm p-6 hover:shadow-md transition-shadow" data-testid="feature-tools">
              <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center mb-4">
                <Wrench className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2 uppercase">500+ Products</h3>
              <p className="text-sm text-slate-600">Extensive catalog of tools and hardware</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-sm p-6 hover:shadow-md transition-shadow" data-testid="feature-support">
              <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2 uppercase">Fast Delivery</h3>
              <p className="text-sm text-slate-600">Quick dispatch and reliable delivery service</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-primary mb-8 uppercase tracking-tight" data-testid="categories-heading">
              Shop by Category
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="group"
                  data-testid={`category-${category.id}`}
                >
                  <div className="aspect-square bg-slate-100 rounded-sm overflow-hidden mb-3">
                    <img
                      src={category.image || 'https://images.unsplash.com/photo-1661078483043-6a586b684f17?w=400'}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-primary uppercase group-hover:text-secondary transition-colors">
                    {category.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-primary uppercase tracking-tight" data-testid="featured-products-heading">
                Featured Products
              </h2>
              <Link to="/products">
                <Button variant="outline" className="gap-2" data-testid="view-all-button">
                  View All <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Home;